import type { ReactNode } from 'react';
import {
    Box,
    Flex,
    FormControl,
    FormHelperText,
    Input,
    InputGroup,
    InputRightElement,
    Kbd,
    Stack,
    Text,
} from '@chakra-ui/react';
import { Card, Icon, SegmentedControl, type IconName } from '../../../ui';
import type { InventoryCategory } from '../../../types/inventory';
import { INVENTORY_CATEGORY_OPTIONS } from '../../../utils/inventoryConstants';
import { inventoryFieldProps } from '../../../utils/inventoryFieldStyles';
import { formatPartNumber } from '../../../functions/formatPartNumber';
import { FieldLabel } from './FieldLabelWithBadge';

interface IdentifyStepProps {
    category: InventoryCategory;
    onCategoryChange: (value: InventoryCategory) => void;
    partNumber: string;
    onPartNumberChange: (value: string) => void;
    onPartNumberBlur: () => void;
    onLookup: () => void;
    onSkip: () => void;
    /** Omit to hide the Import From File row. */
    onImport?: () => void;
}

const FillsInRow = ({ label, hint, barWidth }: { label: string; hint?: string; barWidth: string }) => (
    <Flex align="center" justify="space-between" gap={4}>
        <Text textStyle="bodySm" color="fg.default">
            {label}
            {hint && (
                <Text as="span" color="fg.muted">
                    {' '}
                    · {hint}
                </Text>
            )}
        </Text>
        <Box aria-hidden h={2} w={barWidth} borderRadius="full" bg="bg.subtle" flexShrink={0} />
    </Flex>
);

const OtherWayRow = ({
    icon,
    title,
    description,
    onClick,
}: {
    icon: IconName;
    title: ReactNode;
    description: ReactNode;
    onClick: () => void;
}) => (
    <Flex
        as="button"
        type="button"
        onClick={onClick}
        align="center"
        gap={3}
        w="100%"
        px={2}
        py={2.5}
        borderRadius="md"
        textAlign="left"
        transition="background 0.15s ease"
        _hover={{ bg: 'bg.subtle' }}
        _focusVisible={{ outline: '2px solid', outlineColor: 'brand.solid', outlineOffset: '1px' }}
    >
        <Flex
            w={9}
            h={9}
            flexShrink={0}
            align="center"
            justify="center"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            bg="bg.surface"
            color="fg.default"
        >
            <Icon name={icon} size="md" />
        </Flex>
        <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                {title}
            </Text>
            <Text textStyle="caption" color="fg.muted">
                {description}
            </Text>
        </Box>
        <Icon name="chevronRight" size="md" color="fg.muted" />
    </Flex>
);

const IdentifyStep = ({
    category,
    onCategoryChange,
    partNumber,
    onPartNumberChange,
    onPartNumberBlur,
    onLookup,
    onSkip,
    onImport,
}: IdentifyStepProps) => {
    const hasPartNumber = formatPartNumber(partNumber) !== '';

    return (
        <Flex direction="column" gap={6} py={5} minH="100%">
            <Box>
                <Text textStyle="bodySm" color="fg.muted">
                    Identify the part
                </Text>
                <Text textStyle="h5" color="fg.default" mt={1.5}>
                    Start with the manufacturer part number.
                </Text>
            </Box>

            <FormControl isRequired>
                <FieldLabel>Category</FieldLabel>
                <SegmentedControl<InventoryCategory>
                    size="md"
                    fullWidth
                    aria-label="Category"
                    items={INVENTORY_CATEGORY_OPTIONS.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                    }))}
                    value={category}
                    onChange={onCategoryChange}
                />
            </FormControl>

            <FormControl isRequired>
                <FieldLabel>Mfg. Part Number</FieldLabel>
                <InputGroup size="sm">
                    <Input
                        autoFocus
                        autoComplete="off"
                        fontFamily="mono"
                        value={partNumber}
                        onChange={(e) => onPartNumberChange(e.target.value)}
                        onBlur={onPartNumberBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && hasPartNumber) {
                                e.preventDefault();
                                onLookup();
                            }
                        }}
                        pr="6.5rem"
                        {...inventoryFieldProps}
                    />
                    <InputRightElement w="auto" pr={2} pointerEvents="none">
                        <Kbd
                            fontFamily="body"
                            fontSize="xs"
                            fontWeight="semibold"
                            bg="bg.subtle"
                            color="fg.muted"
                            borderColor="border.default"
                            borderBottomWidth="1px"
                            whiteSpace="nowrap"
                            opacity={hasPartNumber ? 1 : 0.6}
                        >
                            ↵ Look up
                        </Kbd>
                    </InputRightElement>
                </InputGroup>
                <FormHelperText color="fg.muted" fontSize="xs">
                    Dashes, spaces and punctuation are stripped automatically.
                </FormHelperText>
            </FormControl>

            <Card variant="outline" p={4}>
                <Text fontSize="sm" fontWeight="semibold" color="fg.default" mb={3}>
                    What The Lookup Fills In
                </Text>
                <Stack spacing={2.5}>
                    <FillsInRow label="Name" barWidth="45%" />
                    <FillsInRow label="Part type" hint="OEM, A/M, RCY…" barWidth="16%" />
                    <FillsInRow label="Unit cost" barWidth="20%" />
                    <FillsInRow label="Unit" barWidth="14%" />
                    <FillsInRow label="Other part numbers" barWidth="26%" />
                    {category === 'part' && (
                        <FillsInRow label="Vehicle make" hint="added as a tag" barWidth="12%" />
                    )}
                </Stack>
                <Text textStyle="caption" color="fg.muted" mt={3}>
                    If the part is already in your inventory, you&apos;ll be told before you
                    create a duplicate.
                </Text>
            </Card>

            <Box mt="auto" pt={3.5} borderTopWidth="1px" borderColor="border.subtle">
                <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mb={1} px={2}>
                    Other Ways To Add
                </Text>
                {onImport && (
                    <OtherWayRow
                        icon="upload"
                        title="Import from file"
                        description="CSV or XLSX. Upload, map columns, preview, then commit."
                        onClick={onImport}
                    />
                )}
                <OtherWayRow
                    icon="pencil"
                    title="Skip the lookup, enter details manually"
                    description="Shop-made items and consumables without a catalog number"
                    onClick={onSkip}
                />
            </Box>
        </Flex>
    );
};

export default IdentifyStep;
